import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Save, Send, AlertTriangle, Plus, Trash2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { publishDealToSlack } from "@/lib/slackClient";
import { BrandLogo } from "@/components/BrandLogo";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { format } from "@/lib/brand-tokens";
import {
  brands, validationRules, getDealById, updateDeal, createDeal,
} from "@/lib/mock-data";
import { Gated } from "@/components/Gated";
import { toast } from "sonner";

// ADM-03 — Deal create/edit. Each brand within a deal is independently configured
// (rate, cap, face range, per-member cap). Persists via store mutators.

const newBrandConfig = (brandId, isPrimary = false) => ({
  brandId,
  isPrimary,
  rate: 92,
  capTotal: 10000,
  capUsed: 0,
  minFace: 25,
  maxFace: 500,
  perMemberCap: 2500,
});

export default function DealEditPage() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const isEdit = !!dealId;
  const original = isEdit ? getDealById(dealId) : null;

  const [form, setForm] = useState(original ? {
    ...original,
    brandConfigs: (original.brandConfigs && original.brandConfigs.length
      ? original.brandConfigs.map(c => ({ ...c }))
      : [newBrandConfig(original.brandId, true)]),
    faqOverride: original.faqOverride || null,
    whyBuying: original.whyBuying || "",
    isFeatured: !!original.isFeatured,
  } : {
    name: "",
    brandConfigs: [],          // empty until admin adds the first brand
    status: "Draft",
    openedAt: "",
    deadline: "",
    paymentTerms: "Net 7 days from approval",
    validationRuleIds: ["rule_card_length_16", "rule_pin_4_digit", "rule_face_value_in_range", "rule_dup_member"],
    publicNote: "", internalNote: "", whyBuying: "",
    faqOverride: null, isFeatured: false,
  });
  const [retroOpen, setRetroOpen] = useState(false);
  const [pendingAddBrand, setPendingAddBrand] = useState("");

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "object" && v?.target ? v.target.value : v }));
  const toggleRule = (rid) => setForm((f) => ({
    ...f,
    validationRuleIds: f.validationRuleIds.includes(rid)
      ? f.validationRuleIds.filter((x) => x !== rid)
      : [...f.validationRuleIds, rid],
  }));

  // ── Brand-config helpers ────────────────────────────────────────────────
  const updateBrandConfig = (idx, key, value) => setForm((f) => ({
    ...f,
    brandConfigs: f.brandConfigs.map((c, i) =>
      i === idx ? { ...c, [key]: key === "brandId" || key === "isPrimary" ? value : Number(value) || 0 } : c
    ),
  }));
  const removeBrandConfig = (idx) => setForm((f) => {
    const next = f.brandConfigs.filter((_, i) => i !== idx);
    if (next.length && !next.some(c => c.isPrimary)) next[0].isPrimary = true;
    return { ...f, brandConfigs: next };
  });
  const setPrimaryConfig = (idx) => setForm((f) => ({
    ...f,
    brandConfigs: f.brandConfigs.map((c, i) => ({ ...c, isPrimary: i === idx })),
  }));
  const addBrand = (brandId) => {
    if (!brandId) return;
    setForm((f) => {
      if (f.brandConfigs.some(c => c.brandId === brandId)) return f;
      const isFirst = f.brandConfigs.length === 0;
      return { ...f, brandConfigs: [...f.brandConfigs, newBrandConfig(brandId, isFirst)] };
    });
    setPendingAddBrand("");
  };

  const usedBrandIds = new Set(form.brandConfigs.map(c => c.brandId));
  const availableBrands = brands.filter(b => !usedBrandIds.has(b.id));
  const primaryConfig = form.brandConfigs.find(c => c.isPrimary) || form.brandConfigs[0];
  const isMultiBrand = form.brandConfigs.length > 1;
  // Detect ANY brand's rate change after publish (not just primary). Each entry
  // is { brandId, brandName, from, to } so the retroactivity dialog can name names.
  const changedRates = (isEdit && original?.status === "Published")
    ? form.brandConfigs
        .map(cur => {
          const prev = original.brandConfigs?.find(c => c.brandId === cur.brandId);
          if (prev && Number(prev.rate) !== Number(cur.rate)) {
            const b = brands.find(br => br.id === cur.brandId);
            return { brandId: cur.brandId, brandName: b?.name || cur.brandId, from: prev.rate, to: Number(cur.rate) };
          }
          return null;
        })
        .filter(Boolean)
    : [];
  const ratesChanged = changedRates.length > 0;

  // FAQ helpers
  const addFaqItem = () => setForm((f) => ({ ...f, faqOverride: [...(f.faqOverride || []), { q: "", a: "" }] }));
  const updateFaqItem = (i, key, value) => setForm((f) => ({
    ...f,
    faqOverride: (f.faqOverride || []).map((it, idx) => idx === i ? { ...it, [key]: value } : it),
  }));
  const removeFaqItem = (i) => setForm((f) => ({
    ...f,
    faqOverride: (f.faqOverride || []).filter((_, idx) => idx !== i),
  }));
  const clearFaqOverride = () => setForm((f) => ({ ...f, faqOverride: null }));

  const validate = () => {
    if (!form.name?.trim()) { toast.error("Deal name is required"); return false; }
    if (form.brandConfigs.length === 0) { toast.error("Add at least one brand to this deal"); return false; }
    for (const c of form.brandConfigs) {
      if (!c.brandId) { toast.error("Brand row missing brand"); return false; }
      if (!c.rate || c.rate < 50 || c.rate > 100) { toast.error("Each brand rate must be between 50 and 100"); return false; }
      if (!c.capTotal || c.capTotal <= 0) { toast.error("Each brand needs a total cap > $0"); return false; }
    }
    if (!form.deadline) { toast.error("Closing deadline is required"); return false; }
    return true;
  };

  const persist = (publish) => {
    const patch = {
      name: form.name.trim(),
      brandConfigs: form.brandConfigs.map(c => ({
        brandId:      c.brandId,
        isPrimary:    !!c.isPrimary,
        rate:         Number(c.rate),
        capTotal:     Number(c.capTotal),
        capUsed:      Number(c.capUsed) || 0,
        minFace:      Number(c.minFace),
        maxFace:      Number(c.maxFace),
        perMemberCap: Number(c.perMemberCap),
      })),
      status: publish ? "Published" : form.status,
      openedAt: form.openedAt,
      deadline: form.deadline,
      paymentTerms: form.paymentTerms,
      validationRuleIds: form.validationRuleIds,
      publicNote: form.publicNote,
      whyBuying: form.whyBuying,
      faqOverride: Array.isArray(form.faqOverride) && form.faqOverride.length
        ? form.faqOverride.filter((it) => it.q?.trim() || it.a?.trim())
        : null,
      isFeatured: !!form.isFeatured,
      internalNote: form.internalNote,
    };
    if (isEdit) updateDeal(dealId, patch);
    else createDeal(patch);
  };

  const onSave = (publish = false) => {
    if (!validate()) return;
    if (ratesChanged && !retroOpen) { setRetroOpen(true); return; }
    persist(publish);

    // Auto-publish to Slack on a true Draft/Review → Published transition.
    // Fires non-blockingly so a Slack hiccup never blocks the save UX.
    const wasUnpublished = !isEdit || (original?.status !== "Published");
    if (publish && wasUnpublished) {
      const brandsById = Object.fromEntries(brands.map(b => [b.id, b]));
      const dealForSlack = {
        ...form,
        id: dealId || `deal_${Date.now().toString(36)}`,
        rate: form.brandConfigs?.[0]?.rate,
        capTotal: form.brandConfigs?.reduce((s, c) => s + (Number(c.capTotal) || 0), 0),
        capUsed: 0,
        brandIds: form.brandConfigs?.map(c => c.brandId) || [],
        isMultiBrand: (form.brandConfigs?.length || 0) > 1,
        brandId: form.brandConfigs?.[0]?.brandId,
      };
      publishDealToSlack(dealForSlack, brandsById)
        .then((r) => toast.success("Posted to Slack", { description: `#${r.channel || "channel"} · ts=${r.ts}` }))
        .catch((e) => {
          const msg = String(e?.message || e);
          if (msg.includes("not configured")) {
            // Silent — admin hasn't connected Slack yet. No need to scare them.
            return;
          }
          toast.warning("Slack post failed", { description: msg });
        });
    }

    toast.success(`${publish ? "Published" : "Saved"}: ${form.name || "(untitled deal)"}`, {
      description: publish ? "Members will see this deal in the live showcase." : "Draft saved. Publish when you're ready.",
    });
    setTimeout(() => navigate("/admin/deals"), 700);
  };

  return (
    <div data-testid="deal-edit-page" className="space-y-5 pb-10 max-w-[1080px] mx-auto">
      <Link to="/admin/deals" className="inline-flex items-center gap-1.5 text-caption text-ink-subtle hover:text-ink">
        <ChevronLeft className="size-3.5" /> Deals
      </Link>

      <div>
        <div className="eyebrow">{isEdit ? "Edit deal" : "New deal"}</div>
        <h1 className="font-brand text-display text-ink leading-tight mt-1">{isEdit ? form.name || "Untitled deal" : "Create a new deal"}</h1>
        <p className="text-body-sm text-ink-muted mt-1">
          Each brand in the deal has its own rate, cap, and face-value range. Schedule and payment terms are shared.
        </p>
      </div>

      <div className="bg-surface border border-line rounded-xl p-7 space-y-9">
        {/* Status + featured */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 p-1 bg-surface-alt rounded-md w-fit">
            {["Draft", "Review", "Published"].map((s) => (
              <button key={s} type="button" onClick={() => set("status")(s)} data-testid={`status-${s}`}
                      className={`px-4 py-1.5 rounded text-caption font-semibold transition-all ${
                        form.status === s ? "bg-surface text-ink shadow-xs" : "text-ink-muted hover:text-ink"
                      }`}>
                {s}
              </button>
            ))}
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <Switch checked={form.isFeatured} onCheckedChange={(v) => set("isFeatured")(v)} data-testid="featured-toggle" />
            <span className="text-caption font-semibold text-ink inline-flex items-center gap-1">
              <Star className={`size-3.5 ${form.isFeatured ? "fill-amber-400 text-amber-500" : "text-ink-hint"}`} />
              Featured deal
            </span>
          </label>
        </div>

        <Section title="Basics">
          <Field label="Deal name" required>
            <Input value={form.name} onChange={set("name")} placeholder="e.g., Home Depot — June Push" data-testid="deal-name-input" />
          </Field>
        </Section>

        {/* ─── Brand configurations table ─────────────────────────────── */}
        <Section
          title={`Brand configurations${form.brandConfigs.length > 1 ? ` · ${form.brandConfigs.length} brands` : ""}`}
          subtitle={
            isMultiBrand
              ? "Each brand row is configured independently. Members reserve against a specific brand's cap."
              : "Single-brand deal. Add another brand below to make this a multi-brand deal."
          }
        >
          {form.brandConfigs.length === 0 ? (
            <div className="border border-dashed border-line rounded-md p-8 text-center text-body-sm text-ink-muted">
              No brands yet. Add the first brand to get started.
            </div>
          ) : (
            <div className="border border-line rounded-md overflow-hidden bg-surface">
              <div className="grid grid-cols-[2fr_repeat(5,1fr)_auto] gap-x-3 px-4 py-2.5 bg-surface-alt text-eyebrow uppercase text-ink-hint text-[10px] font-semibold">
                <div>Brand</div>
                <div className="text-right">Rate %</div>
                <div className="text-right">Total cap $</div>
                <div className="text-right">Per-member $</div>
                <div className="text-right">Min face $</div>
                <div className="text-right">Max face $</div>
                <div className="w-8"></div>
              </div>
              <div className="divide-y divide-line/60">
                {form.brandConfigs.map((cfg, i) => {
                  const b = brands.find(br => br.id === cfg.brandId);
                  return (
                    <div
                      key={`${cfg.brandId}-${i}`}
                      data-testid={`brand-config-row-${cfg.brandId}`}
                      className={`grid grid-cols-[2fr_repeat(5,1fr)_auto] gap-x-3 px-4 py-3 items-center ${
                        cfg.isPrimary ? "bg-brand-50/30" : ""
                      }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        {b && <BrandLogo brand={b} size="xs" />}
                        <div className="min-w-0 flex-1">
                          <div className="text-body-sm font-semibold text-ink truncate">{b?.name || cfg.brandId}</div>
                          <div className="mt-0.5">
                            {cfg.isPrimary ? (
                              <span className="text-[10px] font-bold uppercase tracking-wide text-brand-700 bg-brand-50 border border-brand-100 px-1.5 py-0.5 rounded">Primary</span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setPrimaryConfig(i)}
                                className="text-[11px] text-ink-muted hover:text-brand-700 underline-offset-2 hover:underline"
                                data-testid={`set-primary-${cfg.brandId}`}
                              >
                                Set primary
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      <NumInput value={cfg.rate} onChange={(v) => updateBrandConfig(i, "rate", v)}
                                min={50} max={100} step={0.5} suffix="%" testid={`rate-${cfg.brandId}`} />
                      <NumInput value={cfg.capTotal} onChange={(v) => updateBrandConfig(i, "capTotal", v)}
                                min={0} step={500} testid={`cap-${cfg.brandId}`} />
                      <NumInput value={cfg.perMemberCap} onChange={(v) => updateBrandConfig(i, "perMemberCap", v)}
                                min={0} step={500} testid={`pmc-${cfg.brandId}`} />
                      <NumInput value={cfg.minFace} onChange={(v) => updateBrandConfig(i, "minFace", v)}
                                min={0} step={5} testid={`min-${cfg.brandId}`} />
                      <NumInput value={cfg.maxFace} onChange={(v) => updateBrandConfig(i, "maxFace", v)}
                                min={0} step={25} testid={`max-${cfg.brandId}`} />
                      <button
                        type="button"
                        onClick={() => removeBrandConfig(i)}
                        disabled={form.brandConfigs.length === 1}
                        className="size-8 grid place-items-center rounded text-ink-hint hover:text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-ink-hint disabled:cursor-not-allowed"
                        aria-label="Remove brand"
                        title={form.brandConfigs.length === 1 ? "A deal needs at least one brand" : "Remove brand"}
                        data-testid={`remove-brand-${cfg.brandId}`}
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Add brand row */}
          {availableBrands.length > 0 && (
            <div className="mt-4 flex items-center gap-2.5">
              <Select value={pendingAddBrand} onValueChange={setPendingAddBrand}>
                <SelectTrigger className="w-[260px]" data-testid="add-brand-select">
                  <SelectValue placeholder="Choose a brand to add…" />
                </SelectTrigger>
                <SelectContent>
                  {availableBrands.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!pendingAddBrand}
                onClick={() => addBrand(pendingAddBrand)}
                data-testid="add-brand-btn"
              >
                <Plus className="size-4 mr-1.5" /> Add brand
              </Button>
              <span className="text-caption text-ink-hint">
                {form.brandConfigs.length === 0
                  ? "Pick the first brand for this deal."
                  : `${availableBrands.length} more brand${availableBrands.length === 1 ? "" : "s"} available.`}
              </span>
            </div>
          )}

          {/* Quick deal-level totals readout */}
          {form.brandConfigs.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-caption">
              <Totals brandConfigs={form.brandConfigs} />
            </div>
          )}
        </Section>

        <Section title="Schedule (shared across all brands)">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Opens at">
              <Input type="datetime-local" value={form.openedAt?.slice(0, 16) || ""} onChange={set("openedAt")} />
            </Field>
            <Field label="Closes at" required>
              <Input type="datetime-local" value={form.deadline?.slice(0, 16) || ""} onChange={set("deadline")} />
            </Field>
            <Field label="Payment terms" required className="sm:col-span-2">
              <Select value={form.paymentTerms} onValueChange={set("paymentTerms")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Net 5 days from approval">Net 5 days from approval</SelectItem>
                  <SelectItem value="Net 7 days from approval">Net 7 days from approval</SelectItem>
                  <SelectItem value="Net 10 days from approval">Net 10 days from approval</SelectItem>
                  <SelectItem value="Net 14 days from approval">Net 14 days from approval</SelectItem>
                  <SelectItem value="Payment within 3 days of approval">Payment within 3 days of approval</SelectItem>
                  <SelectItem value="Payment within 5 days of approval">Payment within 5 days of approval</SelectItem>
                  <SelectItem value="Payment within 5 business days of approval">Payment within 5 business days of approval</SelectItem>
                  <SelectItem value="Payment within 7 days of approval">Payment within 7 days of approval</SelectItem>
                  <SelectItem value="Payment within 10 days of approval">Payment within 10 days of approval</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </Section>

        <Section title="Validation rules (shared)" subtitle={`${form.validationRuleIds.length} of ${validationRules.length} selected. Severity is fixed per rule; only the rule set is editable.`}>
          <ul className="space-y-1 max-h-[340px] overflow-auto border border-line rounded-md divide-y divide-line/60">
            {validationRules.map((r) => {
              const checked = form.validationRuleIds.includes(r.id);
              return (
                <li key={r.id}>
                  <label className="flex items-start gap-3 px-4 py-3 hover:bg-surface-alt/60 cursor-pointer">
                    <Checkbox checked={checked} onCheckedChange={() => toggleRule(r.id)} className="mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-body-sm font-semibold text-ink">{r.name}</span>
                        <span className={`text-caption px-1.5 py-0.5 rounded ${r.severity === "reject" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{r.severity}</span>
                      </div>
                      <div className="text-caption text-ink-subtle">{r.description}</div>
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
        </Section>

        <Section title="Member-facing content" subtitle="Drives what members see on the public deal page.">
          <div className="space-y-5">
            <Field label="Why we're buying" hint="The narrative that explains the demand for this deal. Shows in the 'About this deal' section.">
              <Textarea
                rows={4}
                value={form.whyBuying || ""}
                onChange={set("whyBuying")}
                placeholder="e.g., Home Depot moves an enormous volume of gift cards during the spring renovation push…"
                data-testid="why-buying-input"
              />
            </Field>
            <Field label="Public note" hint="Short callout shown to members on the deal detail page.">
              <Textarea rows={2} value={form.publicNote || ""} onChange={set("publicNote")} placeholder="e.g., Premier seasonal buy. Bring volume." />
            </Field>
            <Field label="Internal note" hint="Admin-only. Hidden from members.">
              <Textarea rows={2} value={form.internalNote || ""} onChange={set("internalNote")} placeholder="e.g., Watch for prefix variants from corporate cards." />
            </Field>
          </div>
        </Section>

        <Section
          title="FAQ override (optional)"
          subtitle={
            Array.isArray(form.faqOverride) && form.faqOverride.length > 0
              ? "Custom Q&A shown on this deal page instead of the default lifecycle FAQ."
              : "Leave empty to use the default deal-lifecycle FAQ. Add items below to override per-deal."
          }
        >
          <div className="space-y-3">
            {(form.faqOverride || []).map((item, i) => (
              <div key={i} data-testid={`faq-item-${i}`} className="border border-line rounded-md p-4 bg-surface-alt/30 space-y-3 relative">
                <button
                  type="button"
                  onClick={() => removeFaqItem(i)}
                  className="absolute top-2 right-2 size-7 rounded grid place-items-center text-ink-hint hover:text-red-600 hover:bg-red-50"
                  aria-label="Remove FAQ item"
                  data-testid={`faq-remove-${i}`}
                >
                  <Trash2 className="size-3.5" />
                </button>
                <Field label={`Question ${i + 1}`}>
                  <Input value={item.q} onChange={(e) => updateFaqItem(i, "q", e.target.value)} placeholder="e.g., How do I submit a digital Steam code?" data-testid={`faq-q-${i}`} />
                </Field>
                <Field label="Answer">
                  <Textarea rows={2} value={item.a} onChange={(e) => updateFaqItem(i, "a", e.target.value)} placeholder="Plain text answer (no markdown)." data-testid={`faq-a-${i}`} />
                </Field>
              </div>
            ))}
            <div className="flex items-center gap-3">
              <Button type="button" variant="outline" size="sm" onClick={addFaqItem} data-testid="faq-add-btn">
                <Plus className="size-4 mr-1.5" /> Add FAQ item
              </Button>
              {Array.isArray(form.faqOverride) && form.faqOverride.length > 0 && (
                <button type="button" onClick={clearFaqOverride} className="text-caption text-ink-muted hover:text-ink underline-offset-2 hover:underline" data-testid="faq-clear-btn">
                  Clear all — use default FAQ
                </button>
              )}
            </div>
          </div>
        </Section>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 sticky bottom-4">
        <div className="text-caption text-ink-subtle">
          {form.brandConfigs.length > 0 && (
            <>Total cap across brands: <span className="font-semibold text-ink tabular-nums">
              {format.currency(form.brandConfigs.reduce((a, c) => a + (Number(c.capTotal) || 0), 0))}
            </span></>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/deals"><Button variant="outline">Cancel</Button></Link>
          <Gated action="deals.edit">
            <Button variant="outline" onClick={() => onSave(false)} data-testid="save-draft-btn">
              <Save className="size-4 mr-1.5" /> Save{form.status === "Published" ? "" : " as draft"}
            </Button>
          </Gated>
          <Gated action="deals.publish">
            <Button onClick={() => onSave(true)} className="bg-brand-700 hover:bg-brand-800 text-white" data-testid="publish-deal-btn">
              <Send className="size-4 mr-1.5" /> Publish
            </Button>
          </Gated>
        </div>
      </div>

      {/* Retroactivity modal — fires when ANY brand's rate changes after publish */}
      <Dialog open={retroOpen} onOpenChange={setRetroOpen}>
        <DialogContent>
          <DialogHeader>
            <div className="size-10 rounded-md bg-amber-50 text-amber-700 grid place-items-center mb-3">
              <AlertTriangle className="size-5" />
            </div>
            <DialogTitle>
              {changedRates.length === 1
                ? `${changedRates[0].brandName} rate changed — apply retroactively?`
                : `${changedRates.length} brand rates changed — apply retroactively?`}
            </DialogTitle>
            <DialogDescription>
              Existing reservations on this deal are affected based on your choice. Member-facing notifications will be sent accordingly.
            </DialogDescription>
          </DialogHeader>
          <ul className="bg-surface-alt border border-line rounded-md divide-y divide-line/60" data-testid="retro-changes-list">
            {changedRates.map((c) => (
              <li key={c.brandId} className="px-3.5 py-2.5 flex items-center justify-between gap-3" data-testid={`retro-change-${c.brandId}`}>
                <span className="text-body-sm font-medium text-ink truncate">{c.brandName}</span>
                <span className="text-caption tabular-nums">
                  <span className="text-ink-subtle">{c.from}%</span>
                  <span className="mx-1.5 text-ink-hint">&rarr;</span>
                  <span className="text-ink font-semibold">{c.to}%</span>
                </span>
              </li>
            ))}
          </ul>
          <div className="flex flex-col gap-3 mt-2">
            <button onClick={() => { setRetroOpen(false); persist(true); toast.success(`Published: ${form.name}`, { description: "Existing reservations kept their original rate." }); setTimeout(() => navigate("/admin/deals"), 700); }}
                    data-testid="retro-new-only-btn"
                    className="text-left p-4 rounded-md border border-line hover:border-brand-700 hover:bg-brand-50/40 transition-all">
              <div className="text-body-sm font-semibold text-ink">New reservations only</div>
              <div className="text-caption text-ink-subtle">Existing reservations on the changed brand{changedRates.length > 1 ? "s" : ""} keep their original rate. Recommended for fairness.</div>
            </button>
            <button onClick={() => { setRetroOpen(false); persist(true); toast.success(`Published: ${form.name} (retroactive)`, { description: `Bumped ${changedRates.length} brand rate${changedRates.length === 1 ? "" : "s"} on existing reservations.` }); setTimeout(() => navigate("/admin/deals"), 700); }}
                    data-testid="retro-apply-all-btn"
                    className="text-left p-4 rounded-md border border-line hover:border-amber-500 hover:bg-amber-50/40 transition-all">
              <div className="text-body-sm font-semibold text-ink">Apply to all reservations</div>
              <div className="text-caption text-ink-subtle">All current and future reservations on the affected brand{changedRates.length > 1 ? "s" : ""} move to the new rate. Logged with member notification.</div>
            </button>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setRetroOpen(false)}>Cancel</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section>
      <h2 className="font-brand text-h2 text-ink">{title}</h2>
      {subtitle && <p className="mt-1 text-caption text-ink-subtle leading-relaxed">{subtitle}</p>}
      <div className="mt-5">{children}</div>
    </section>
  );
}
function Field({ label, required, hint, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-caption font-semibold text-ink mb-1.5 block">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      {children}
      {hint && <p className="mt-1.5 text-caption text-ink-hint leading-relaxed">{hint}</p>}
    </div>
  );
}
function NumInput({ value, onChange, min, max, step, suffix, testid }) {
  return (
    <div className="relative">
      <Input
        type="number" min={min} max={max} step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 text-right tabular-nums pr-1.5"
        data-testid={testid}
      />
      {suffix && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-caption text-ink-hint pointer-events-none">{suffix}</span>}
    </div>
  );
}
function Totals({ brandConfigs }) {
  const totalCap = brandConfigs.reduce((a, c) => a + (Number(c.capTotal) || 0), 0);
  const minRate = Math.min(...brandConfigs.map(c => Number(c.rate) || 0));
  const maxRate = Math.max(...brandConfigs.map(c => Number(c.rate) || 0));
  const rateLabel = minRate === maxRate ? `${maxRate}%` : `${minRate}–${maxRate}%`;
  const minFace = Math.min(...brandConfigs.map(c => Number(c.minFace) || 0));
  const maxFace = Math.max(...brandConfigs.map(c => Number(c.maxFace) || 0));
  return (
    <>
      <Pill label="Rates" value={rateLabel} />
      <Pill label="Total cap" value={format.currency(totalCap)} />
      <Pill label="Face value" value={`$${minFace}–$${maxFace}`} />
    </>
  );
}
function Pill({ label, value }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-surface-alt border border-line">
      <span className="text-ink-subtle">{label}:</span>
      <span className="text-ink font-semibold tabular-nums">{value}</span>
    </span>
  );
}
